/* ---------------------------------------------------------------------------
   Construction Monitoring: one structure's daily returns, a month at a time.

   Each day is drawn as its weather — the sky the return recorded, with the
   people on site and the rain — so a wet fortnight reads as a wet fortnight
   before anybody reads a figure. A day with no return is left plain and says
   so; that gap is itself something a reader of the diary is looking for.

   Picking a day opens its return: the one on record, to correct, or a new one
   for that day. The same sheet the Daily return button opens, on another date.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, can, type ReturnLayout, type State } from '../core/store.ts';
import { seg } from '../components/widgets.ts';
import { weatherChip, weatherGlyph, weatherKey, weatherSky, wxAttrs } from '../components/weather.ts';
import { prettyDate } from '../data/derive.ts';
import { trimNumber } from '../data/quantities.ts';
import { WEATHER, weatherLabel, weatherObstructs } from '../data/siteReturn.ts';
import type { SiteReturn } from '../data/types.ts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Today, as a calendar day. The one place this module reads the clock. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthName(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/** The month before or after, as `yyyy-mm`. */
function stepMonth(month: string, by: 1 | -1): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

/** People and rain, as the cell and its title print them. */
function figures(r: SiteReturn): string[] {
  return [
    r.labour === null ? null : `${r.labour} on site`,
    r.rainfallMm == null ? null : `${trimNumber(r.rainfallMm)} mm`,
    r.pending ? 'queued' : null,
  ].filter((x): x is string => x !== null);
}

/** The panel header's control: calendar or table. */
export function returnActions(s: Readonly<State>): HTMLElement[] {
  const layouts: { layout: ReturnLayout; label: string }[] = [
    { layout: 'calendar', label: 'Calendar' },
    { layout: 'list', label: 'List' },
  ];
  return [
    seg(
      layouts.map((l) => ({
        label: l.label,
        on: s.returnLayout === l.layout,
        onClick: () => actions.setReturnLayout(l.layout),
      })),
    ),
  ];
}

/** The month's returns as a calendar, or null where there is none to draw. */
export function returnsCalendar(s: Readonly<State>): HTMLElement | null {
  if (s.progressRead !== 'read' || s.selectedId === null) return null;
  const bridgeId = s.selectedId;
  const returns = s.siteReturns.filter((r) => r.bridgeId === bridgeId);
  if (returns.length === 0) return null;

  const byDay = new Map(returns.map((r) => [r.returnedOn, r]));
  const days = [...byDay.keys()].sort();
  const firstDay = days[0]!;
  const now = today();
  const first = firstDay.slice(0, 7);
  const latest = days[days.length - 1]!.slice(0, 7);
  /* On to this month, so today can be recorded from here. */
  const last = now.slice(0, 7) > latest ? now.slice(0, 7) : latest;
  const month = s.returnMonth !== null && s.returnMonth >= first && s.returnMonth <= last ? s.returnMonth : latest;
  const mayRecord = can(s, 'progress.record');

  const start = `${month}-01`;
  const lead = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7;
  const [y, m] = month.split('-').map(Number) as [number, number];
  const length = new Date(Date.UTC(y, m, 0)).getUTCDate();

  const cells: HTMLElement[] = [];
  for (let i = 0; i < lead; i++) cells.push(h('div', { class: 'rcal__day rcal__day--pad' }));
  let lost = 0;
  let missing = 0;
  const tally = new Map<string, number>();
  for (let d = 1; d <= length; d++) {
    const iso = `${month}-${String(d).padStart(2, '0')}`;
    const r = byDay.get(iso);
    const future = iso > now;
    /* A day with no return only counts as missing inside the diary's life:
       after its first return and not yet to come. */
    const gap = !r && !future && iso >= firstDay;
    if (r) {
      const key = weatherKey(r.weather);
      tally.set(key, (tally.get(key) ?? 0) + 1);
      if (weatherObstructs(r.weather)) lost++;
    } else if (gap) missing++;

    const title = r
      ? [prettyDate(iso), weatherLabel(r.weather), ...figures(r), mayRecord ? 'Open this day’s return' : null]
          .filter(Boolean)
          .join('\n')
      : `${prettyDate(iso)}\n${future ? 'To come' : 'No return'}${mayRecord && !future ? '\nRecord this day’s return' : ''}`;
    const open = mayRecord && !future;
    cells.push(
      h(
        open ? 'button' : 'div',
        {
          ...(open ? { type: 'button', onclick: () => actions.openSiteReturn(bridgeId, iso) } : {}),
          class: ['rcal__day', future && 'rcal__day--future', iso === now && 'rcal__day--today'],
          ...(r ? wxAttrs(r.weather) : {}),
          title,
          'aria-label': title.replace(/\n/g, ', '),
        },
        r ? weatherSky() : null,
        h(
          'span',
          { class: 'rcal__head' },
          h('span', { class: 'rcal__num num', text: String(d) }),
          r && weatherObstructs(r.weather) ? h('span', { class: 'rcal__lost', text: 'lost' }) : null,
          r ? weatherGlyph(r.weather, 18) : null,
        ),
        r ? h('span', { class: 'rcal__what', text: weatherLabel(r.weather) }) : null,
        r && figures(r).length ? h('span', { class: 'rcal__meta num', text: figures(r).join(' · ') }) : null,
        gap ? h('span', { class: 'rcal__none', text: 'No return' }) : null,
      ),
    );
  }
  while (cells.length % 7 !== 0) cells.push(h('div', { class: 'rcal__day rcal__day--pad' }));

  const notes = [
    lost ? `${lost} ${lost === 1 ? 'day' : 'days'} of weather that stops work` : null,
    missing ? `${missing} ${missing === 1 ? 'day' : 'days'} without a return` : null,
  ].filter(Boolean);

  return h(
    'div',
    { class: 'pcal rcal' },
    h(
      'div',
      { class: 'pcal__bar' },
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Previous month',
          'aria-label': 'Previous month',
          disabled: month <= first,
          onclick: () => actions.setReturnMonth(stepMonth(month, -1)),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8, class: 'pcal__back' }),
      ),
      h('h3', { class: 'pcal__month', text: monthName(month) }),
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Next month',
          'aria-label': 'Next month',
          disabled: month >= last,
          onclick: () => actions.setReturnMonth(stepMonth(month, 1)),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8 }),
      ),
      h(
        'div',
        { class: 'rcal__tally', style: 'margin-left:auto' },
        ...WEATHER.filter((w) => tally.has(w.id)).map((w) =>
          /* Not marked lost: the note beside them counts those days. */
          weatherChip(w.id, { detail: String(tally.get(w.id)), lost: false }),
        ),
        notes.length ? h('span', { class: 'rcal__tally-note', text: notes.join(' · ') }) : null,
      ),
    ),
    h(
      'div',
      { class: 'pcal__grid', 'aria-label': `Daily returns in ${monthName(month)}` },
      ...WEEKDAYS.map((w) => h('div', { class: 'pcal__wd', text: w })),
      ...cells,
    ),
  );
}
